import { useCallback, useState } from 'react';

import RumClient from '../../../monitoring/RumClient';
import { useOptionalCurrentUser } from '../../../contexts/UserContext';

/**
 * Feedback content provided alongside a rating value.
 *
 * - `string` — free-form text (e.g. from a textarea modal).
 * - `Record<string, unknown>` — structured data (e.g. from a multi-field form).
 *
 * This union allows future feedback modals to submit structured form data
 * without breaking existing consumers that expect plain text.
 */
export type FeedbackComments = string | Record<string, unknown>;

/** Stable empty array to avoid defeating useCallback memoization when no prompt values are provided. */
const EMPTY_FEEDBACK_PROMPT: readonly never[] = [];

/**
 * Props accepted by the shared RUM feedback view model.
 *
 * - `TValue` constrains the set of possible rating values (e.g. `'POSITIVE' | 'NEGATIVE'`).
 * - `TFeedback` constrains the shape of follow-up feedback (e.g. `string` for
 *   free-form text, or a structured interface for form data).
 */
export interface RumFeedbackViewModelProps<
  TValue extends string,
  TFeedback extends FeedbackComments = string,
> {
  /** RUM event type string passed to `RumClient.recordEvent()`. */
  readonly eventType: string;
  /**
   * Compose the RUM event payload for a rating action or follow-up feedback.
   *
   * Called by the component to obtain the payload to record. The component
   * enriches the returned object with `userId` before passing it to
   * `RumClient.recordEvent`. CloudWatch RUM automatically attaches a
   * timestamp to every event.
   *
   * - Initial click: `composeEvent(value)` — `feedback` is `undefined`.
   * - Follow-up submission: `composeEvent(value, feedback)` — `feedback`
   *   contains the user's comments or structured form data.
   */
  readonly composeEvent: (value: TValue, feedback?: TFeedback) => Record<string, unknown>;
  /**
   * Which rating values should trigger the feedback modal after selection.
   * Only shown when the array is non-empty.
   * Defaults to an empty array (no modal).
   */
  readonly feedbackPromptValues?: readonly TValue[];
}

/** Read-only presentation state exposed by the view model. */
export interface RumFeedbackViewModel<
  TValue extends string,
  TFeedback extends FeedbackComments = string,
> {
  /** The currently-selected rating value, or `null` if none selected. */
  readonly currentValue: TValue | null;
  /** Whether the feedback modal is currently visible. */
  readonly isModalVisible: boolean;
  /** Select a rating value — emits the RUM event and updates state. */
  readonly select: (value: TValue) => void;
  /** Submit follow-up feedback — emits the RUM event and closes the modal. */
  readonly submitFeedback: (comments: TFeedback) => void;
  /** Dismiss the feedback modal without submitting. */
  readonly dismissModal: () => void;
}

/**
 * Hook that manages feedback state and emits CloudWatch RUM events.
 *
 * Designed to be rating-type agnostic: the visual variant (thumbs, stars)
 * provides the `TValue` and `TFeedback` constraints and renders accordingly,
 * while this hook handles the shared logic of tracking selection, showing the
 * modal, and calling `RumClient.recordEvent`.
 *
 * Every emitted event is automatically enriched with `userId`.
 */
export function useRumFeedbackViewModel<
  TValue extends string,
  TFeedback extends FeedbackComments = string,
>(props: RumFeedbackViewModelProps<TValue, TFeedback>): RumFeedbackViewModel<TValue, TFeedback> {
  const { eventType, composeEvent, feedbackPromptValues = EMPTY_FEEDBACK_PROMPT } = props;
  const currentUser = useOptionalCurrentUser();
  const userId = currentUser?.userId;

  const [currentValue, setCurrentValue] = useState<TValue | null>(null);
  const [isModalVisible, setIsModalVisible] = useState(false);

  const emit = useCallback(
    (payload: Record<string, unknown>) => {
      RumClient.recordEvent(eventType, {
        ...payload,
        ...(userId != null && { userId }),
      });
    },
    [eventType, userId],
  );

  const select = useCallback(
    (value: TValue) => {
      setCurrentValue(value);

      const payload = composeEvent(value);
      emit(payload);

      if (feedbackPromptValues.includes(value)) {
        setIsModalVisible(true);
      }
    },
    [composeEvent, feedbackPromptValues, emit],
  );

  const submitFeedback = useCallback(
    (comments: TFeedback) => {
      if (currentValue === null) return;

      const payload = composeEvent(currentValue, comments);
      emit(payload);
      setIsModalVisible(false);
    },
    [composeEvent, currentValue, emit],
  );

  const dismissModal = useCallback(() => {
    setIsModalVisible(false);
  }, []);

  return {
    currentValue,
    isModalVisible,
    select,
    submitFeedback,
    dismissModal,
  };
}
